import { useState, useEffect, useCallback, useRef } from 'react';
import { store as rtkStore } from 'redux/store';
import { linkpayApi } from 'redux/services/linkpayApi';
import isEqual from 'react-fast-compare';
import { addListener } from '@reduxjs/toolkit';

export const useGetLinkpaysListQuery = (params, config) => {
    const [resp, setResp] = useState(undefined);
    const { getLinkpaysList } = linkpayApi.endpoints;
    const [fetchConfig, setFetchConfig] = useState(config);
    const subRef = useRef();

    const fetch = useCallback(
      async () => {
        const { pollingInterval } = fetchConfig || 0;
        const pollingConfig = pollingInterval ? {
            subscribe: true,
            subscriptionOptions: {
                pollingInterval,
            },
        } : {};
        subRef?.current?.unsubscribe();
        subRef.current = rtkStore.dispatch(getLinkpaysList.initiate({ params }, {
            forceRefetch: true,
            ...fetchConfig,
            ...pollingConfig,
        }));
        const response = await subRef.current;
        setResp(response);

      },
      [params, fetchConfig, getLinkpaysList],
    );

    useEffect(() => {
        if(!isEqual(config, fetchConfig)) {
            setFetchConfig(config);
        }
    }, [config, fetchConfig]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    useEffect(() => {

        return () => {
            subRef?.current?.unsubscribe();

        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const unsubscribe = rtkStore.dispatch(
            addListener({
                predicate: () => true,
                effect: async (action, listenerApi) => {
                    const { type, payload } = action;
                    const isFullfilled = type.includes(linkpayApi.reducerPath) && type.includes('fulfilled');
                    if (isFullfilled) {
                        const { queryCacheKey } = subRef.current;
                        const cache = await listenerApi.getState()[linkpayApi.reducerPath].queries;
                        const prevTime = resp?.fulfilledTimeStamp;
                        const currentTime = cache[queryCacheKey].fulfilledTimeStamp;
                        const isError = payload?.error;
                        if (!isError && currentTime && prevTime && currentTime > prevTime) {
                            setResp({
                                ...cache[queryCacheKey],
                                isLoading: false,
                            });
                        }
                    }
                },
            })
        );
        return () => {
            (typeof unsubscribe === 'function') && unsubscribe();
        }
    }, [resp]);

    return  {
        ...resp,
        refetch: fetch,
    };
};

export const useGetLinkpayDetailQuery = (params, config) => {
    const [resp, setResp] = useState(undefined);
    const { getLinkpayDetail } = linkpayApi.endpoints;
    const [fetchConfig, setFetchConfig] = useState(config);
    const fetch = useCallback(
        async () => {
            const response = await rtkStore.dispatch(getLinkpayDetail.initiate(params, {
                forceRefetch: true,
                ...fetchConfig,
            }));
            setResp(response);
        },
        [params, fetchConfig, getLinkpayDetail],
    );

    useEffect(() => {
        if(!isEqual(config, fetchConfig)) {
            setFetchConfig(config);
        }
    }, [config, fetchConfig]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    return  {
        ...resp,
        refetch: fetch,
    };
};

export const useCreateLinkpayMutation = () => {
    const { createLinkpay } = linkpayApi.endpoints;
    const fetch = useCallback(async (params) => {
        return await rtkStore.dispatch(createLinkpay.initiate({ params }));

    }, [createLinkpay]);

    return [
        fetch,
    ];
};

export const useUpdateLinkpayDetailMutation = () => {
    const { updateLinkpayDetail } = linkpayApi.endpoints;
    const [result, setResult] = useState();
    const fetch = useCallback(async (params) => {
        const response =  await rtkStore.dispatch(updateLinkpayDetail.initiate({ params }));
        setResult(response);
        return response;
    }, [updateLinkpayDetail]);

    return [
        fetch,
        result,
    ];
};

export const useLazyGetLinkpayInvoiceQuery = (options) => {
    const { getLinkpayInvoice } = linkpayApi.endpoints;
    const [result, setResult] = useState();
    const fetch = useCallback(async (params) => {
        const response =  await rtkStore.dispatch(getLinkpayInvoice.initiate({ params }, options));
        setResult(response);
        return response;
    }, [options, getLinkpayInvoice]);


    return [
        fetch,
        result,
    ];
};


export const useReissueLinkpayInvoiceMutation = () => {
    const { reissueLinkpayInvoice } = linkpayApi.endpoints;
    const [result, setResult] = useState();
    const fetch = useCallback(async (params) => {
        const response =  await rtkStore.dispatch(reissueLinkpayInvoice.initiate({ params }));
        setResult(response);
        return response;
    }, [reissueLinkpayInvoice]);

    return [
        fetch,
        result,
    ];
};
